import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { DeskGroupsPutBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import {
  deskGroupsOptions,
  errorMessage,
  groupsListOptions,
} from '@/features/groups/groups-queries';

/**
 * Grupos de atendentes vinculados à mesa (GET|PUT /desks/:id/groups — PUT
 * substitui os vínculos). Sem grupos = mesa visível a todos os atendentes.
 */
export function DeskGroupsSection({ deskId }: { deskId: number }) {
  const queryClient = useQueryClient();
  const [groups, linked] = useQueries({
    queries: [groupsListOptions(), deskGroupsOptions(deskId)],
  });

  const save = useMutation({
    mutationFn: async (body: DeskGroupsPutBody) => {
      const res = await api<unknown>(`/desks/${deskId}/groups`, { method: 'PUT', body });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deskGroupsOptions(deskId).queryKey });
    },
  });

  const linkedIds = new Set((linked.data ?? []).map((g) => g.id));

  const toggle = (groupId: number, checked: boolean) => {
    const next = new Set(linkedIds);
    if (checked) next.add(groupId);
    else next.delete(groupId);
    save.mutate({ groupIds: [...next] });
  };

  const pending = groups.isPending || linked.isPending;
  const error = groups.error ?? linked.error;

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-1 flex items-center gap-2">
        <h2 className="text-base font-semibold">Grupos de atendentes</h2>
        {linked.data ? (
          <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
            {linked.data.length}
          </span>
        ) : null}
      </div>
      <p className="mb-3 text-[13px] text-muted-foreground">
        Sem grupos vinculados, a mesa fica visível a todos. Com grupos, apenas membros e
        administradores a enxergam.
      </p>
      {pending ? (
        <div className="space-y-2" aria-busy>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : groups.isError || linked.isError ? (
        <EmptyState className="h-24" message={`Erro ao carregar grupos: ${errorMessage(error)}`} />
      ) : (groups.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhum grupo cadastrado.{' '}
          <Link to="/settings/groups" className="font-medium text-primary-pressed hover:underline">
            Criar grupo
          </Link>
        </p>
      ) : (
        <ul className="space-y-1.5">
          {(groups.data ?? []).map((g) => (
            <li key={g.id}>
              <label
                className={cn(
                  'flex cursor-pointer items-center gap-2.5 rounded-md border border-divider px-3 py-2 text-sm transition-colors hover:border-border',
                  save.isPending && 'pointer-events-none opacity-60'
                )}
              >
                <Checkbox
                  checked={linkedIds.has(g.id)}
                  disabled={save.isPending}
                  onCheckedChange={(checked) => toggle(g.id, checked === true)}
                />
                <span className="min-w-0 truncate font-medium">{g.name}</span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
                  {g.memberIds.length === 1 ? '1 membro' : `${g.memberIds.length} membros`}
                </span>
                {!g.active ? (
                  <span className="shrink-0 text-xs text-muted-foreground">Inativo</span>
                ) : null}
              </label>
            </li>
          ))}
        </ul>
      )}
      {save.error ? <p className="mt-2 text-sm text-destructive">{errorMessage(save.error)}</p> : null}
    </section>
  );
}
